import React, { useMemo } from 'react';
import { Menu as AntdMenu, Badge } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { UserRole } from '../../enums/userRoles.enum';
import { selectActiveMenuGroup, selectActiveMenuItem, selectIsLightTheme, selectIsMenuCollapsed, selectNoActionReportCount, selectOpenedKeys } from '../../global-store/global.selectors';
import { selectEnabledAiFeatures, selectRole } from '../../../pages/utillity-pages/login/store/auth.selectors';
import { getMenus } from './config/menu-utils';
import { MenuItem } from './models/menuItem.model';
import { AlarmReportsPath } from '../../../app/path';
import { setActiveMenuGroupKey, setOpenedKeys } from '../../global-store/global.slice';

const TruncatedLabel: React.FC<{ text: string; to?: string }> = ({ text, to }) => {
    const ref = React.useRef<HTMLElement>(null);
    const [tooltipTitle, setTooltipTitle] = React.useState<string | undefined>(undefined);

    const handleMouseEnter = () => {
        const el = ref.current;
        if (el && el.scrollWidth > el.clientWidth) {
            setTooltipTitle(text);
        } else {
            setTooltipTitle(undefined);
        }
    };

    const style: React.CSSProperties = {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };

    if (to) {
        return (
            <Link to={to} ref={ref as React.RefObject<HTMLAnchorElement>} title={tooltipTitle} onMouseEnter={handleMouseEnter} style={style}>
                {text}
            </Link>
        );
    }

    return (
        <span ref={ref as React.RefObject<HTMLSpanElement>} title={tooltipTitle} onMouseEnter={handleMouseEnter} style={style}>
            {text}
        </span>
    );
};

const Menu = () => {
    const dispatch = useDispatch();
    const activeMenuItem = useSelector(selectActiveMenuItem);
    const activeMenuGroup = useSelector(selectActiveMenuGroup);
    const isLightTheme = useSelector(selectIsLightTheme);
    const role = useSelector(selectRole);
    const openedKeys = useSelector(selectOpenedKeys);
    const noActionReportCount = useSelector(selectNoActionReportCount);
    const isMenuCollapsed = useSelector(selectIsMenuCollapsed);
    const enabledAiFeatures = useSelector(selectEnabledAiFeatures);
    let navigate = useNavigate();
    const { t } = useTranslation();
    const items = useMemo(() => getMenus().filter(x =>
        x.roles.some(y => y === role) &&
        (!x.requiredAiFeature || role === UserRole.SuperAdmin || enabledAiFeatures.includes(x.requiredAiFeature))
    ), [role, enabledAiFeatures]);

    const rootSubmenuKeys = useMemo(
      () => items.filter(i => i.children?.length).map(i => i.id.toString()),
      [items]
    );

    const onOpenChange = (keys: string[]) => {
      const latestOpenKey = keys.find(k => !openedKeys.includes(k));
  
      if (!latestOpenKey) {
        dispatch(setOpenedKeys([]));
        dispatch(setActiveMenuGroupKey(null));
        return;
      }
  
      if (rootSubmenuKeys.includes(latestOpenKey)) {
        // accordion: sadece 1 root açık
        dispatch(setOpenedKeys([latestOpenKey]));
      } else {
        dispatch(setOpenedKeys(keys));
      }
    };

    const getIcon = (item: MenuItem) => {
        if(item.link === AlarmReportsPath)
        {
            return <Badge style={{ fontSize: "0.875rem" }} size="small" className='flex justify-center items-center text-sm' count={noActionReportCount}>{React.createElement(item.icon)}</Badge>
        } else if (item.label === "alarmsOverview") {
            return <Badge dot={noActionReportCount > 0}>{React.createElement(item.icon)}</Badge>
            
        }
        return React.createElement(item.icon);
    }

    const menuItems = useMemo(() => {
        return items.filter(x => !x.isDisabled).map(
            (item) => ({
                key: item.id,
                icon: getIcon(item),
                label: item.children?.length === 0 ? (
                    <TruncatedLabel to={item.link} text={t(`menu.${item.link}`) as string} />
                ) : (
                    <TruncatedLabel text={t(`menu.${item.label}`) as string} />
                ),
                children: item.children?.length !== 0 ? item.children?.filter(x =>
                    x.roles.filter(y => y === role).length > 0 &&
                    (!x.requiredAiFeature || role === UserRole.SuperAdmin || enabledAiFeatures.includes(x.requiredAiFeature))
                ).map((children) => ({
                    key: children.id,
                    icon: getIcon(children),
                    label: children.children?.length === 0 ? (
                        <TruncatedLabel to={children.link} text={t(`menu.${children.label ?? children.link}`) as string} />
                    ) : (
                        <TruncatedLabel text={t(`menu.${children.label}`) as string} />
                    ),
                    onClick: children.children?.length === 0 ? () => navigate(children.link) : undefined,
                    type: children.children?.length === 0 ? undefined : "group",
                    children: children.children?.length !== 0 ? children.children?.filter(x =>
                        x.roles.filter(y => y === role).length > 0 &&
                        (!x.requiredAiFeature || role === UserRole.SuperAdmin || enabledAiFeatures.includes(x.requiredAiFeature))
                    ).map((grandChildren) => ({
                        key: grandChildren.id,
                        icon: getIcon(grandChildren),
                        label: (
                            <TruncatedLabel to={grandChildren.link} text={t(`menu.${grandChildren.link}`) as string} />
                        ),
                        onClick: () => navigate(grandChildren.link)
                    })) : undefined,
                })) : undefined,
                onClick: item.children?.length === 0 ? () => navigate(item.link) : undefined,
            }),
        );
    }, [items, role, noActionReportCount, t, navigate, enabledAiFeatures]);

    return (
        <AntdMenu theme={isLightTheme ? "light" : "dark"} mode="inline"
            selectedKeys={activeMenuItem ? [activeMenuItem.toString()] : []}
            openKeys={isMenuCollapsed ? undefined : (openedKeys.length ? openedKeys : (activeMenuGroup ? [activeMenuGroup.toString()] : []))}
            onOpenChange={onOpenChange}
            getPopupContainer={() => document.body}
            triggerSubMenuAction="click"
            items={menuItems}
        />
    );
}

export default Menu;